import React, { useEffect, useMemo, useState } from 'react';
import './MyAppsScreen.css';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import backIcon from '../assets/backspace-icon.png';
import placeholderLogo from '../assets/placeholder-logo.png';
import deleteOutlineIcon from '../assets/delete-outline-icon.png';
import DeleteAppDraftDrawer from '../components/popups/DeleteAppDraftDrawer';

function getVerifyLabel(status) {
  if (!status) return 'pending';
  return String(status).toLowerCase();
}

function getStatusLabel(status) {
  if (!status) return 'draft';
  return String(status).toLowerCase();
}

function formatDomain(app) {
  return app?.domain || app?.appUrl || '—';
}

function MyAppsScreen({ user }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [appProfiles, setAppProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [refreshing, setRefreshing] = useState(false);

const [showDeleteDraftDrawer, setShowDeleteDraftDrawer] = useState(false);
const [targetDraftId, setTargetDraftId] = useState('');

  const isAppAccount = user?.accountType === 'app';


  const fetchApps = async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError('');
      const res = await api.get('/apps/me');
      const nextApps = Array.isArray(res.data?.appProfiles) ? res.data.appProfiles : [];
      setAppProfiles(nextApps);
    } catch (err) {
      console.error('❌ Failed to fetch app profiles:', err);
      setError(err?.response?.data?.message || 'Failed to load your apps.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchApps();
  }, []);

  useEffect(() => {
    if (location.state?.refreshApps) {
      fetchApps(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const setupApps = useMemo(() => {
    return appProfiles.filter((app) => getStatusLabel(app.status) !== 'active');
  }, [appProfiles]);

  const activeApps = useMemo(() => {
    return appProfiles.filter((app) => getStatusLabel(app.status) === 'active');
  }, [appProfiles]);

  const handleContinueSetup = (appId) => {
    navigate('/my-apps/new', {
      state: {
        draftId: appId,
        mode: 'draft-edit',
      },
    });
  };

  const handleOpenAppDetail = (app) => {
    navigate(`/my-apps/${app._id}`, {
      state: { app },
    });
  };



const handleDeleteDraft = (e, appId) => {
    e.stopPropagation();
    setTargetDraftId(appId);
    setShowDeleteDraftDrawer(true);
  };

  const confirmDeleteDraft = async () => {
    if (!targetDraftId) return;

    try {
      setDeletingId(targetDraftId);
      await api.delete(`/apps/${targetDraftId}`);
      setAppProfiles((prev) => prev.filter((app) => app._id !== targetDraftId));
      setShowDeleteDraftDrawer(false);
      setTargetDraftId('');
    } catch (err) {
      console.error('❌ Failed to delete app draft:', err);
      alert(err?.response?.data?.message || 'Failed to delete app draft.');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <div className="myapps-screen">
      <div className="myapps-topbar">


<button className="myapps-back-btn" onClick={() => navigate('/home')}>
  <img src={backIcon} alt="Back" />
</button>

        <h1>My Apps</h1>
      </div>

      <div className="myapps-content">


        {!isAppAccount && (
          <div className="myapps-hero-card">
            <div className="myapps-hero-text">
              <h2>Register and manage your apps</h2>
              <p>
                Create an app profile, verify your domain, and activate your app identity inside BarterFi.
              </p>
            </div>

            <button
              className="myapps-primary-btn"
              onClick={() => navigate('/my-apps/new')}
            >
              Register New App
            </button>
          </div>
        )}



        {isAppAccount && (
          <div className="myapps-state-card">
            <p>You are currently using an app identity.</p>
          </div>
        )}

{!loading && !error && isAppAccount && (
          <div className="myapps-state-card">
            <p>Switch to your main account to view and manage app profiles.</p>
          </div>
        )}

        {loading && (
          <div className="myapps-state-card">
            <p>Loading your apps...</p>
          </div>
        )}

        {!loading && error && (
          <div className="myapps-state-card error">
            <p>{error}</p>
            <button className="myapps-secondary-btn" onClick={() => fetchApps()}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && !isAppAccount && (
   
       <>
            <section className="myapps-section">
              <div className="myapps-section-head">
                <h3>Setup Needed</h3>
                <span>{setupApps.length}</span>
              </div>

              {setupApps.length === 0 ? (
                <div className="myapps-empty-card">
                  <p>No apps waiting for setup.</p>
                </div>
              ) : (
                <div className="myapps-list">
                  {setupApps.map((app) => {
                    const isDeleting = deletingId === app._id;
                    const verificationStatus = getVerifyLabel(app.domainVerificationStatus);
                    const status = getStatusLabel(app.status);

                    return (
                      <div
                        className={`myapps-card myapps-card-clickable ${isDeleting ? 'is-deleting' : ''}`}
                        key={app._id}
                        onClick={() => !isDeleting && handleContinueSetup(app._id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && !isDeleting) {
                            handleContinueSetup(app._id);
                          }
                        }}
                      >
                        <div className="myapps-card-left">
                          <img
                            src={app.profilePic || placeholderLogo}
                            alt={app.name || 'App logo'}
                            className="myapps-card-logo"
                          />

                          <div className="myapps-card-meta">
                            <h4>{app.name || 'Untitled App'}</h4>
                            <p>@{app.username || 'no_username'}</p>
                            <p className="myapps-domain">{formatDomain(app)}</p>
                          </div>
                        </div>

                        <div className="myapps-card-right">
                          <button
                            type="button"
                            className="myapps-draft-delete-button"
                            onClick={(e) => handleDeleteDraft(e, app._id)}
                            disabled={isDeleting}
                            title="Delete draft"
                            aria-label="Delete draft"
                          >
                            <img src={deleteOutlineIcon} alt="Delete draft" />
                          </button>

                          <div className={`app-badge status-${status}`}>
                            {status}
                          </div>

                          <div className={`app-badge verify-${verificationStatus}`}>
                            {verificationStatus}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="myapps-section">
              <div className="myapps-section-head">
                <h3>Active Apps</h3>
                <span>{activeApps.length}</span>
              </div>

              {activeApps.length === 0 ? (
                <div className="myapps-empty-card">
                  <p>No active apps yet.</p>
                </div>
              ) : (
                <div className="myapps-list">
                  {activeApps.map((app) => {
                    const verificationStatus = getVerifyLabel(app.domainVerificationStatus);
                    const status = getStatusLabel(app.status);

                    return (
                      <div
                        className="myapps-card myapps-card-clickable"
                        key={app._id}
                        onClick={() => handleOpenAppDetail(app)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleOpenAppDetail(app);
                          }
                        }}
                      >
                        <div className="myapps-card-left">
                          <img
                            src={app.profilePic || placeholderLogo}
                            alt={app.name || 'App logo'}
                            className="myapps-card-logo"
                          />

                          <div className="myapps-card-meta">
                            <h4>{app.name || 'Untitled App'}</h4>
                            <p>@{app.username || 'no_username'}</p>
                            <p className="myapps-domain">{formatDomain(app)}</p>

                            <div className="myapps-inline-note">
                              {app.linkedUser
                                ? 'Linked sub-account ready.'
                                : 'Active app. Linked sub-account data not returned yet.'}
                            </div>
                          </div>
                        </div>

                        <div className="myapps-card-right">
                          <div className={`app-badge status-${status}`}>
                            {status}
                          </div>

                          <div className={`app-badge verify-${verificationStatus}`}>
                            {verificationStatus}
                          </div>

                          {app.linkedUser ? (
                            <div className="app-badge linked-ready">sub-account ready</div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <div className="myapps-footer-actions">
              <button
                className="myapps-secondary-btn"
                onClick={() => fetchApps(true)}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </>
        )}
      </div>


{showDeleteDraftDrawer && (
  <DeleteAppDraftDrawer
    onClose={() => {
      setShowDeleteDraftDrawer(false);
      setTargetDraftId('');
    }}
    onConfirm={confirmDeleteDraft}
  />
)}


    </div>
  );
}

export default MyAppsScreen;
